'use client';

import { tabsAnatomy } from '@chakra-ui/anatomy';
import {
  Tabs as ChakraTabs,
  Show,
  StyleProps,
  SystemStyleObject,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  UseTabsProps,
  createMultiStyleConfigHelpers,
  theme
} from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import { isUndefined } from 'lodash';

import Icon, { IconName } from '@/components/atomic/v1/icon/Icon';
import { slugify } from '@/utils/contentful';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(tabsAnatomy.keys);

function setTabUrl(tabsId: string, tabTitle: string) {
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.set('tab', slugify(tabTitle));
  window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}#${tabsId}`);
}

const selectedStyles: StyleProps = {
  backgroundColor: 'status/information/text',
  borderColor: 'status/information/text',
  color: 'white'
};

const boxed = definePartsStyle({
  root: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center'
  },
  tab: {
    borderRadius: '8px',
    flexDirection: 'column',
    borderBottom: '0px',
    border: '2px solid',
    borderColor: 'transparent',
    margin: '-2px',
    _selected: {
      ...selectedStyles,
      _hover: selectedStyles // On hover, if the tab is selected, keep the selected styles - don't change to the hover styles.
    },
    _hover: {
      borderColor: 'border/hover',
      color: 'border/hover',
      backgroundColor: 'background/secondary'
    }
  },
  tablist: {
    borderBottom: 'none',
    border: '2px solid',
    borderColor: 'border/primary',
    borderRadius: '12px',
    display: { base: 'grid', md: 'inline-flex' },
    gap: { base: 2, md: 4 },
    gridTemplateColumns: { base: 'repeat(2, 1fr)', md: 'unset' }
  },
  tabpanels: {
    paddingTop: 8
  },
  tabpanel: {
    padding: 0
  }
});

const activeNavItemStyles: SystemStyleObject = {
  position: 'relative',
  _after: {
    content: '""',
    position: 'absolute',
    height: 1,
    width: '100%',
    bgColor: 'orange.100',
    bottom: '-4px',
    borderRadius: '4px 4px 0px 0px'
  }
};

const anchorNavbar = definePartsStyle({
  root: {
    backgroundColor: 'white',
    boxShadow: 'card.light',
    px: { base: 4, md: 8 },
    py: 1,
    minW: '360px',
    overflow: 'auto',
    position: 'sticky',
    top: 0,
    width: '100%'
  },
  tablist: {
    columnGap: 6,
    width: { base: 'max-content', lg: 'fit-content' },
    mx: { xl: 'auto' },
    maxWidth: '1500px'
  },
  tab: {
    px: 4,
    py: 3,
    textAlign: 'center',
    color: 'text/body',
    fontWeight: 'medium',
    _selected: activeNavItemStyles,
    // Only apply the hover styles on devices with a primary input mechanism of a mouse - not a touchscreen. This is to prevent the tab on touchscreen devices from having the hover style.
    // How it works: https://developer.mozilla.org/en-US/docs/Web/CSS/@media/pointer
    '@media(pointer: fine)': {
      _hover: activeNavItemStyles
    }
  }
});

const simple = definePartsStyle({
  ...theme.components.Tabs.variants?.line({
    colorScheme: 'text/body',
    colorMode: 'light',
    theme: {}
  }),
  tab: {
    px: 3,
    borderTopRadius: 4,
    _selected: {
      fontWeight: 'bold',
      position: 'relative',
      _after: {
        content: '""',
        position: 'absolute',
        height: 1,
        width: '100%',
        bgColor: 'border/active',
        bottom: '-1px',
        borderTopRadius: 4
      }
    },
    _hover: {
      borderColor: 'border/hover',
      color: 'border/hover',
      backgroundColor: 'background/secondary'
    },
    _focusVisible: {
      boxShadow: 'none',
      borderBottomRadius: 4,
      borderColor: 'border/hover',
      color: 'border/hover',
      backgroundColor: 'background/secondary',
      outline: '2px solid',
      outlineColor: 'border/active',
      _after: {
        borderBottomRadius: 4
      }
    }
  }
});

const enclosed = definePartsStyle({
  root: {
    py: 1,
    position: 'sticky',
    top: 0
  },
  tablist: {
    borderBottom: '4px solid',
    border: 'none'
  },
  tab: {
    minWidth: '120px',
    width: 'auto',
    flexShrink: 0,
    px: 3,
    py: 2,
    borderBottom: '4px solid',
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: 'none',
    background: 'transparent',
    marginBottom: 0,
    position: 'relative'
  }
});

export const tabsTheme = defineMultiStyleConfig({
  variants: {
    boxed,
    anchorNavbar,
    simple,
    enclosed
  },
  defaultProps: {
    variant: 'boxed'
  }
});

type TabItemWithIcon = {
  title: string;
  content: ReactNode;
  icon: IconName;
};

type TabItemWithoutIcon = {
  title: string;
  content: ReactNode;
};

interface TabsProps {
  tabItems: Array<TabItemWithIcon> | Array<TabItemWithoutIcon>;
  largeTabs?: boolean;
  id?: UseTabsProps['id'];
  tabProps?: TabProps;
  defaultIndex?: number;
  alignStart?: boolean;
}

/**
 * Tabs myOMERS component
 *
 * The Tabs component groups titles and their corresponding content. It also handles its mobile responsiveness - shrink the viewport width to see what it would look like on mobile (or small) screens.
 *
 * To put an icon in the tab title, use the `iconName` key and set it to a valid icon name in all the objects passed to the `tabItems` prop. The icon would only show up for large desktop tabs (it is hidden for smaller screens).
 *
 * Additional note: All tabs must either have an icon or have no icon. There shouldn't be some tabs with an icon and others without.
 */
const Tabs = ({ largeTabs, id, tabProps, defaultIndex, alignStart, ...restProps }: TabsProps) => {
  // Since the type of tabItems is an array of two different objects, TypeScript seemingly can't determine the specific type of object in the array. The below function tells TypeScript when tabItems has objects each with an icon key or not.
  const tabItemsHaveIcon = (
    tabItems: Array<TabItemWithIcon> | Array<TabItemWithoutIcon>
  ): tabItems is Array<TabItemWithIcon> => !!tabItems[0] && 'icon' in tabItems[0];

  let tabTitles: Array<JSX.Element>;

  if (tabItemsHaveIcon(restProps.tabItems)) {
    tabTitles = restProps.tabItems.map((tabItem) => {
      const { title, icon } = tabItem;

      const paddingForLargeTabs = largeTabs ? '24px' : '8px';

      return (
        <Tab
          key={title}
          fontWeight="bold"
          fontSize={{
            base: 'regular',
            md: largeTabs || icon ? 'large' : 'regular'
          }}
          px={{ base: '16px', md: icon || largeTabs ? '24px' : '16px' }}
          py={{ base: '8px', md: icon ? '12px' : paddingForLargeTabs }}
          {...tabProps}
          data-testid={`tab-button-${title}`}
        >
          <Show above="md">
            <Icon name={icon} size={48} pb="8px" />
          </Show>
          {title}
        </Tab>
      );
    });
  } else {
    tabTitles = restProps.tabItems.map((tabItem) => {
      const { title } = tabItem;

      return (
        <Tab
          key={title}
          fontWeight="bold"
          fontSize={{
            base: 'regular',
            md: largeTabs ? 'large' : 'regular'
          }}
          px={{ base: '16px', md: largeTabs ? '24px' : '16px' }}
          py={{ base: '8px', md: largeTabs ? '24px' : '8px' }}
          onClick={() => setTabUrl(id || '', title)}
          {...tabProps}
          data-testid={`tab-button-${title}`}
        >
          {title}
        </Tab>
      );
    });
  }

  const tabContent = restProps.tabItems.map(({ content, title }) => <TabPanel key={title}>{content}</TabPanel>);

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const urlTab = new URLSearchParams(window.location.search).get('tab');

    if (urlTab && isUndefined(defaultIndex)) {
      setTabIndex(
        Math.max(
          tabTitles.findIndex((tab) => {
            if (tab.key) {
              return slugify(tab.key.toString()) === slugify(urlTab);
            }

            return false;
          }),
          0
        )
      );
    }
  }, [tabTitles, defaultIndex]);

  return (
    // @ts-ignore The ChakraTabs API allows for an undefined `id`, but causes a type error due to `exactOptionalPropertyTypes`
    <ChakraTabs
      id={id}
      index={tabIndex}
      onChange={(i) => {
        setTabIndex(i);
      }}
      defaultIndex={defaultIndex}
      {...(alignStart ? { alignItems: 'flex-start' } : {})}
    >
      <TabList width={{ base: '100%', md: 'fit-content' }}>{tabTitles}</TabList>
      <TabPanels>{tabContent}</TabPanels>
    </ChakraTabs>
  );
};

export { Tabs };
